import {
  Card,
  CopyTextField,
  Text,
  TextField,
  TextFieldVariant,
  Tooltip,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import {
  CredentialTableFields,
  IntegrationSetupStepType,
  ReportFields,
  SecretFieldType,
} from "../../../config/types";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import IntegrationReportFieldsTable from "./IntegrationReportFieldsTable";
import { Inputs } from "./MainConnectorSetupStepBase";

type Props = {
  inputs?: Inputs;
  currentStepType?: IntegrationSetupStepType;
  mergeToken?: string;
  ipAddresses?: string[];
  credentialTableFields?: CredentialTableFields;
  setCredentialTableFields?: (newCredentialTableFields: CredentialTableFields) => void;
  reportFields?: ReportFields;
  setIsStepComplete?: (isStepComplete: boolean) => void;
};

const ConnectorSetupStepForm = ({
  currentStepType,
  inputs,
  mergeToken,
  ipAddresses,
  credentialTableFields,
  reportFields,
}: Props) => {
  const { translate } = useAgentHandlerLinkModalContext();

  return (
    <div className="px-5 flex-shrink-0">
      {inputs?.map((input, index) => {
        const isLast = index === inputs.length - 1;

        // Render Typeahead for select fields
        if (input.fieldType === SecretFieldType.SELECT && input.selectOptions) {
          const selectedOption = input.selectOptions.find(opt => opt.value === input.value);

          return (
            <div key={index} className={`group ${isLast ? "mb-1" : "mb-4"}`}>
              <Typeahead
                options={input.selectOptions}
                value={selectedOption}
                onChange={(_event, option) => input.setValue(option?.value ?? "")}
                getOptionLabel={(option) => option.label}
                placeholder={input.placeholder}
                labelSubtitle={input.labelSubtitle}
                borderVariant={TextFieldVariant.Bordered}
              />
            </div>
          );
        }

        // Default: render TextField for text fields
        return (
          <div key={index} className={`group ${isLast ? "mb-1" : "mb-4"}`}>
            <TextField
              onChange={(e) => input.setValue(e.target.value)}
              value={input.value as string | undefined}
              placeholder={input.placeholder}
              prefix={
                input.prefix && (
                  <div className="flex items-center rounded-bl-md rounded-tl-md bg-gray-10 max-w-20 px-3 py-2">
                    <div
                      className="block overflow-hidden text-ellipsis text-gray-40 whitespace-nowrap text-center"
                      dir="rtl"
                    >
                      <bdi>{input.prefix}</bdi>
                    </div>
                  </div>
                )
              }
              postfix={
                input.suffix && (
                  <Tooltip title={input.suffix}>
                    <div className="flex items-center rounded-br-md rounded-tr-md bg-gray-10 max-w-[120px] px-3 py-2 transition-[max-width] duration-200 ease-out group-focus-within:max-w-16">
                      <div className="block overflow-hidden text-ellipsis text-gray-40 whitespace-nowrap text-center">
                        {input.suffix}
                      </div>
                    </div>
                  </Tooltip>
                )
              }
              variant={TextFieldVariant.Bordered}
              type={input.isRedacted ? "password" : "text"}
              error={input.error != null}
              errorText={input.error}
              labelSubtitle={input.labelSubtitle}
            />
          </div>
        );
      })}
      {currentStepType === IntegrationSetupStepType.STEP_TYPE_ORG_PURCHASE && (
        <>
          <Card variant="outline" className="w-full mb-4 p-2.5 border-gray-10">
            <Text className="flex justify-center pb-2" variant="h6">
              {translate("token-text", "Token: ")}
            </Text>
            {mergeToken && <CopyTextField text={mergeToken} />}
          </Card>
        </>
      )}
      {ipAddresses &&
        ipAddresses?.length > 0 &&
        currentStepType === IntegrationSetupStepType.STEP_TYPE_WHITELIST_IP_ADDRESS && (
          <>
            <Card variant="outline" className="flex flex-col max-h-60 w-full mb-4 border-gray-10">
              <Text className="flex justify-center p-2.5" variant="h6">
                {ipAddresses.length > 1
                  ? translate("ip-addresses-text", "IP addresses:")
                  : translate("ip-address-text", "IP address:")}
              </Text>
              <div className="flex flex-col overflow-y-auto gap-y-2 mb-2.5 px-2.5">
                {ipAddresses?.length > 0 &&
                  ipAddresses.map((ipAddress: string) => <CopyTextField text={`${ipAddress}`} />)}
              </div>
            </Card>
          </>
        )}

      {(currentStepType === IntegrationSetupStepType.STEP_TYPE_SFTP_CREDENTIALS ||
        currentStepType === IntegrationSetupStepType.STEP_TYPE_SFTP_CREDENTIALS_V2 ||
        currentStepType === IntegrationSetupStepType.STEP_TYPE_SERVICE_ACCOUNT) &&
        credentialTableFields}

      {currentStepType === IntegrationSetupStepType.STEP_TYPE_SFTP_REQUIRED_FIELDS &&
        reportFields && <IntegrationReportFieldsTable reportFields={reportFields} />}
    </div>
  );
};

export default ConnectorSetupStepForm;
